import CloseIcon from '@mui/icons-material/Close'
import InfoIcon from '@mui/icons-material/Info'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import Container from '@mui/material/Container'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import KNCaption from 'components/KNCaption/KNCaption'
import KNLoader from 'components/KNLoader/KNLoader'
import { TripDetailsContext } from 'context/trips/TripDetailsContext'
import { analyticsEvent, analyticsPageView } from 'global/helpers/analytics'
import { combineQueries } from 'global/helpers/queries'
import { getRequestErrorMessage } from 'global/helpers/requestError'
import { sleep } from 'global/helpers/sleep'
import { type ReactElement, type SyntheticEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { driversQueryOptions } from 'screens/DriverManager/DriverManager.service'
import TripAcceptance from 'screens/TripDashboard/TripAcceptance'
import { processDriversAndVehicles, processTrackingInformation } from 'screens/TripDashboard/TripDashboard.helpers'
import {
  sendTripAcceptance,
  tripQueryOptions,
  TRIPS_QUERY_KEY,
  tripTrackingInformationQueryOptions,
} from 'screens/TripDashboard/TripDashboard.service'
import type { TripData } from 'screens/TripDashboard/TripDashboard.types'
import { isStopsViewAvailable } from 'screens/TripDetails/TripDetails.helpers'
import {
  TRIP_DETAILS_QUERY_KEY,
  tripLegsQueryOptions,
  tripLogsQueryOptions,
  tripParkingLocationsQueryOptions,
} from 'screens/TripDetails/TripDetails.service'
import { vehiclesQueryOptions } from 'screens/VehicleManager/VehicleManager.service'
import LocationsMapLibreView from './MapView/LocationsMapLibreView'
import SequenceMapLibreView from './MapView/SequenceMapLibreView'
import ShipmentsView from './ShipmentsView/ShipmentsView'
import StopsView from './StopsView/StopsView'
import type { ParkingLocation } from './TripDetails.types'
import TripLogs from './TripLogs'
import TripParkingLocations from './TripParkingLocations'
import TripSummary from './TripSummary'
import ViewSwitcher from './ViewSwitcher'

const combineTripQueries = combineQueries(['vehicles', 'drivers', 'trip', 'legs'])
const combineSidebarQueries = combineQueries(['logs', 'parkingLocations'])

const TripDetails = (): ReactElement => {
  const theme = useTheme()
  const queryClient = useQueryClient()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { t } = useTranslation()
  const { tripCid } = useParams<{
    tripCid: string
  }>()
  const [tripDetailsState, tripDetailsDispatch] = useContext(TripDetailsContext)
  const [sidebarView, setSidebarView] = useState<'milestones' | 'parking'>('milestones')
  const tripQueries = useQueries({
    queries: [
      vehiclesQueryOptions,
      driversQueryOptions,
      tripQueryOptions(tripCid),
      tripLegsQueryOptions(tripCid),
    ],
    combine: combineTripQueries,
  })
  const sidebarQueries = useQueries({
    queries: [
      tripLogsQueryOptions(tripCid),
      tripParkingLocationsQueryOptions(tripCid),
    ],
    combine: combineSidebarQueries,
  })
  const trackingInformationQuery = useQuery(tripTrackingInformationQueryOptions())
  const [filteredParkingLocations, setFilteredParkingLocations] = useState<ParkingLocation[]>([])

  const processedTrip = useMemo(() => {
    if (!tripQueries.isSuccess) {
      return undefined
    }
    return processTrackingInformation(
      processDriversAndVehicles(tripQueries.data.trip, tripQueries.data.vehicles, tripQueries.data.drivers),
      trackingInformationQuery.data ?? [],
    )
  }, [tripQueries, trackingInformationQuery.data])

  useEffect(() => {
    setFilteredParkingLocations(() => {
      if (!sidebarQueries.isSuccess) {
        return []
      }
      return sidebarQueries.data.parkingLocations.filter((parkingLocation) =>
        tripDetailsState.parkingLocationTypes.includes(parkingLocation.type),
      )
    })
  }, [sidebarQueries.data, tripDetailsState.parkingLocationTypes])

  const stopsViewAvailable = useMemo(() => processedTrip ? isStopsViewAvailable(processedTrip) : false, [processedTrip])

  const refreshTripDetails = useCallback(async (): Promise<void> => {
    await sleep(2000)
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [TRIP_DETAILS_QUERY_KEY, tripCid] }),
      queryClient.invalidateQueries({ queryKey: [TRIPS_QUERY_KEY] }),
    ])
  }, [queryClient, tripCid])
  const tripAcceptanceMutation = useMutation({
    mutationFn: ({ status }: { status: 'ACCEPTED' | 'DECLINED' }) => sendTripAcceptance(tripCid, { status }),
    onSuccess: refreshTripDetails,
  })

  const handleTripOnChange = useCallback(async (updatedTrip?: TripData): Promise<void> => {
    await refreshTripDetails()
  }, [refreshTripDetails])

  const handleTripAcceptanceChange = useCallback(async (updatedTrip: TripData, action: string): Promise<void> => {
    if (action === 'trip_accepted') {
      await tripAcceptanceMutation.mutateAsync({ status: 'ACCEPTED' })
      analyticsEvent('polestar_cs_trip_acceptance', ['accepted'])
    } else if (action === 'trip_declined') {
      await tripAcceptanceMutation.mutateAsync({ status: 'DECLINED' })
      analyticsEvent('polestar_cs_trip_acceptance', ['declined'])
    }
  }, [tripAcceptanceMutation])

  const handleSidebarViewOnChange = useCallback((_event: SyntheticEvent, value: 'milestones' | 'parking') => {
    setSidebarView(value)
  }, [])

  useEffect(() => {
    if (tripDetailsState.parkingLocationFocus) {
      setSidebarView('parking')
    }
  }, [tripDetailsState.parkingLocationFocus])

  useEffect(() => {
    analyticsPageView('polestar/cs/trip_details')
  }, [])

  useEffect(() => {
    if (!processedTrip) {
      return
    }
    if (isMobile && stopsViewAvailable) {
      tripDetailsDispatch({ type: 'setDetailsView', payload: 'stops' })
    }
    if (!stopsViewAvailable) {
      tripDetailsDispatch({ type: 'setDetailsView', payload: 'shipments' })
    }
  }, [isMobile, processedTrip, stopsViewAvailable])

  return (
    <Container
      maxWidth="xxl"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
      }}
    >
      {tripQueries.isPending && (
        <KNLoader>
          <Typography>{t('screens.cs.trip_details.loading')}</Typography>
        </KNLoader>
      )}
      {tripQueries.isError && (
        <Alert severity="error">{getRequestErrorMessage(tripQueries.error)}</Alert>
      )}
      {tripQueries.isSuccess && processedTrip && (
        <Stack spacing={2} sx={{ flexGrow: 1 }}>
          <TripAcceptance trip={processedTrip} onChange={handleTripAcceptanceChange} />
          <Stack
            direction={{ xs: 'column-reverse', lg: 'row' }}
            spacing={{ xs: 2, lg: 4 }}
            sx={{
              position: 'relative',
              marginX: {
                xs: '-0.5rem !important',
                md: '0 !important',
              },
              flexGrow: 1,
            }}
          >
            {tripDetailsState.detailsView === 'shipments' && (
              <ShipmentsView
                trip={processedTrip}
                legs={tripQueries.data.legs}
              />
            )}
            {tripDetailsState.detailsView === 'stops' && (
              <StopsView
                trip={processedTrip}
                legs={tripQueries.data.legs}
              />
            )}
            {tripDetailsState.detailsView === 'sequence_map' && (
              <SequenceMapLibreView
                trip={processedTrip}
                legs={tripQueries.data.legs}
                tripLogs={sidebarQueries.data?.logs ?? []}
                parkingLocations={filteredParkingLocations}
              />
            )}
            {tripDetailsState.detailsView === 'locations_map' && (
              <LocationsMapLibreView
                trip={processedTrip}
                legs={tripQueries.data.legs}
                tripLogs={sidebarQueries.data?.logs ?? []}
                parkingLocations={filteredParkingLocations}
              />
            )}

            <Stack
              spacing={2}
              sx={{
                position: { lg: 'sticky' },
                maxWidth: { lg: '360px' },
                flex: { lg: '0 0 360px' },
                height: { xs: 'auto', md: 'calc(100vh - 5rem)' },
                maxHeight: { xs: '40rem', md: 'none' },
                top: '4rem',
                minHeight: 0,
                zIndex: 1000,
              }}
            >
              {!isMobile && (
                <ViewSwitcher stopsViewAvailable={stopsViewAvailable} />
              )}
              <TripSummary
                trip={processedTrip}
                legs={tripQueries.data.legs}
                onChange={handleTripOnChange}
              />
              <Paper
                sx={{
                  flexGrow: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {sidebarQueries.isPending && (
                  <KNLoader />
                )}
                {sidebarQueries.isSuccess && (
                  <>
                    {sidebarQueries.data.parkingLocations.length > 0 && (
                      <Tabs
                        variant="fullWidth"
                        value={sidebarView}
                        onChange={handleSidebarViewOnChange}
                      >
                        <Tab
                          value="milestones"
                          label={t('screens.cs.trip_details.sidebar.milestones')}
                          sx={{ fontSize: 'small' }}
                        />
                        <Tab
                          value="parking"
                          label={(
                            <Stack direction="row" spacing={1} alignItems="center">
                              <span>{t('screens.cs.trip_details.sidebar.parking')}</span>
                              <Chip label={sidebarQueries.data.parkingLocations.length} size="small" />
                            </Stack>
                          )}
                          sx={{ fontSize: 'small' }}
                        />
                      </Tabs>
                    )}
                    {sidebarView === 'milestones' && (
                      <TripLogs
                        legs={tripQueries.data.legs}
                        tripLogs={sidebarQueries.data.logs}
                        stopsViewAvailable={stopsViewAvailable}
                      />
                    )}
                    {sidebarView === 'parking' && sidebarQueries.data.parkingLocations.length > 0 && (
                      <TripParkingLocations
                        trip={processedTrip}
                        parkingLocations={sidebarQueries.data.parkingLocations}
                        filteredParkingLocations={filteredParkingLocations}
                        stopsViewAvailable={stopsViewAvailable}
                      />
                    )}
                  </>
                )}
              </Paper>
              <KNCaption icon={<InfoIcon />}>{t('screens.cs.trip_details.local_time')}</KNCaption>
            </Stack>
            <Link to={'/trips'} style={{ textDecoration: 'none' }}>
              <IconButton
                aria-label="close"
                sx={{
                  m: -0.75,
                }}
              >
                <CloseIcon />
              </IconButton>
            </Link>
          </Stack>
        </Stack>
      )}
    </Container>
  )
}

export default TripDetails
